import React, { useState, useContext } from 'react';
import axios from 'axios';
import {
  Alert,
  Card,
  CardContent,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Stack,
  TextField,
  InputAdornment,
  Snackbar,
} from '@mui/material';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import SensorsIcon from '@mui/icons-material/Sensors';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningIcon from '@mui/icons-material/Warning';
import SearchIcon from '@mui/icons-material/Search';
import moment from 'moment/moment';
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import { Icon } from 'leaflet';
import { LanguageContext } from '../../../context/languageContext';
import { validateAndSanitizeHostname } from './utilFunctions';
import { useAuth } from '../../../context/authContext';

const VentusSearch = () => {
  const { user } = useAuth();
  const [serial, setSerial] = useState('');
  const [device, setDevice] = useState(null);
  const [cellularCheck, setCellularCheck] = useState(null);
  const [cellularCheckPerformed, setCellularCheckPerformed] = useState(false);
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setIsSnackbarOpen(false);
  };
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rebootLoading, setRebootLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const { translations } =
    useContext(LanguageContext);

  // map marker
  const customIcon = new Icon({
    iconUrl: 'https://cdn-icons-png.flaticon.com/128/684/684908.png',
    iconSize: [38, 38],
  });

  // Searching for Device Information
  const handleSearch = async () => {
    if (!serial) {
      setSnackbarMessage('Serial Number is required for search');
      setSnackbarSeverity('info');
      setIsSnackbarOpen(true);
      return;
    }

    setSearchLoading(true);
    setCellularCheck(null); // Reset health check value on new search
    setCellularCheckPerformed(false); // hide health check on new search

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_ENDPOINT_AGENTUTILS}/comm/ventus/${serial}`,
        {
          device: 'Ventus',
          userId: user.userId,
        }
      );
      setDevice(res.data.data);
      setLat(res.data.data.unit.lat ?? null);
      setLng(res.data.data.unit.lng ?? null);
      setSnackbarMessage('Device Search Successful');
      setSnackbarSeverity('success');
      setIsSnackbarOpen(true);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setSnackbarMessage(
          'Device Not Found, Please Check Serial Number and Device Type (Ventus or Hughes)'
        );
        setSnackbarSeverity('warning');
        setIsSnackbarOpen(true);
      } else {
        console.error('Error fetching device data:', error);
        setSnackbarMessage(
          'Error Fetching Device Data, Please Try Again. If Problem Persists, Contact Support.'
        );
        setSnackbarSeverity('error');
        setIsSnackbarOpen(true);
      }

      setDevice(null);
    } finally {
      // Set a timeout to re-enable the search button after 5 seconds
      setTimeout(() => {
        setSearchLoading(false);
      }, 5000);
    }
  };

  // Clearing Search Results
  const handleClear = () => {
    setSerial('');
    setDevice(null);
    setCellularCheck(null);
    setCellularCheckPerformed(false);
    setLat('');
    setLng('');
    setSnackbarMessage('Device Search Cleared');
    setSnackbarSeverity('info');
    setIsSnackbarOpen(true);
  };

  // Health Check
  const handleCellularCheck = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_ENDPOINT_AGENTUTILS}/comm/ventus/cellularCheck/${device.unit.serial}`
      );
      setCellularCheck(res.data.data.active.rsrp);
      setCellularCheckPerformed(true);
      setSnackbarMessage('Health Check Successful');
      setSnackbarSeverity('success');
      setIsSnackbarOpen(true);
    } catch (error) {
      console.error('Error Health Check on device:', error);
      setSnackbarMessage('Error Performing Health Check on Device');
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
      setCellularCheck(null);
      setCellularCheckPerformed(false);
    }
  };

  // Pinging Device
  const handlePing = async () => {
    try {
      setSnackbarMessage('Pinging Device...');
      setSnackbarSeverity('info');
      setIsSnackbarOpen(true);
      await axios.post(
        `${process.env.REACT_APP_ENDPOINT_AGENTUTILS}/comm/ventus/ping/${device.unit.hostname}`
      );
      setSnackbarMessage('Ping Successful');
      setSnackbarSeverity('success');
      setIsSnackbarOpen(true);
    } catch (error) {
      console.error('Error pinging device:', error);
      setSnackbarMessage('Ping Unsuccessful');
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
    }
  };

  // Reboot Device
  const handleReboot = () => {
    setDialogOpen(true);
  };

  const handleConfirmReboot = async () => {
    try {
      setRebootLoading(true); // start spinner

      let sanitizedHostnameForReboot;
      try {
        sanitizedHostnameForReboot = validateAndSanitizeHostname(
          device.unit.hostname
        ).toUpperCase();
      } catch (err) {
        console.error(`Invalid hostname: ${err.message}`);
        setRebootLoading(false);
        return;
      }

      const rebootData = {
        hostname: sanitizedHostnameForReboot,
        serialNumber: `${device.unit.serial}`,
        softwareVersion: `${device.unit.kernelVersion}`,
        jurisdiction: device.siteInfo.state,
        device: 'Ventus',
        rebooted: 'YES',
        userId: `${user.userId}`,
      };

      await axios.post(
        `${process.env.REACT_APP_ENDPOINT_AGENTUTILS}/comm/ventus/reboot/confirmed`,
        // `http://localhost:8080/v1/engage/contactcenter/comm/ventus/reboot/confirmed`,
        rebootData,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      setDialogOpen(false);
      setSnackbarMessage('Rebooting Device. Please wait 3 minutes.');
      setSnackbarSeverity('info');
      setIsSnackbarOpen(true);

      // Spinner for 3 minutes then stop
      setTimeout(() => {
        setRebootLoading(false);
      }, 180000);
    } catch (error) {
      console.error('Error submitting reboot data:', error);
      setRebootLoading(false); // stop spinner if error
      setSnackbarMessage('Error Rebooting Device');
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
    }
  };

  const handleCancelReboot = async () => {
    try {
      let sanitizedHostnameForCancel;
      try {
        sanitizedHostnameForCancel = validateAndSanitizeHostname(
          device.unit.hostname
        ).toUpperCase();
      } catch (err) {
        console.error(`Invalid hostname: ${err.message}`);
        return;
      }

      const rebootData = {
        hostname: sanitizedHostnameForCancel,
        serialNumber: `${device.unit.serial}`,
        softwareVersion: `${device.unit.kernelVersion}`,
        jurisdiction: device.siteInfo.state,
        device: 'Ventus',
        rebooted: 'NO',
        userId: `${user.userId}`,
      };

      await axios.post(
        `${process.env.REACT_APP_ENDPOINT_AGENTUTILS}/comm/ventus/reboot/cancelled`,
        // `http://localhost:8080/v1/engage/contactcenter/comm/ventus/reboot/cancelled`,
        rebootData,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      setDialogOpen(false);
      setSnackbarMessage('Device Reboot Rejected');
      setSnackbarSeverity('info');
      setIsSnackbarOpen(true);
    } catch (error) {
      console.error('Error submitting reboot data:', error);
      setSnackbarMessage('Error Rejecting Device Reboot');
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
    }
  };

  return (
    <>
      <div className='container' style={{ display: 'flex' }}>
        <div style={{ flex: 1, padding: '10px' }}>
          <Stack
            direction='row'
            spacing={2}
            sx={{
              marginBottom: '20px',
            }}
          >
            <Button
              onClick={handleSearch}
              variant='outlined'
              color='primary'
              disabled={rebootLoading || searchLoading}
              sx={{ fontFamily: 'BrightstarSans-Regular' }}
            >
              {searchLoading ? 'Searching...' : translations.search || 'Search'}
            </Button>
            <Button
              onClick={handleClear}
              variant='outlined'
              color='warning'
              disabled={rebootLoading}
              sx={{ fontFamily: 'BrightstarSans-Regular' }}
            >
              {translations.clear || 'Clear'}
            </Button>
          </Stack>
          <TextField
            type='text'
            value={serial}
            onChange={e => setSerial(e.target.value)}
            placeholder={translations.searchSerialNumber || 'Search'}
            variant='outlined'
            fullWidth
            sx={{
              fontFamily: 'BrightstarSans-Regular',
              backgroundColor: '#ffffff',
              marginBottom: '50px',
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton onClick={handleSearch} aria-label='search'>
                    <SearchIcon
                      sx={{
                        color: '#053469',
                      }}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            label={translations.serialNumber || 'Serial Number'}
          />
          <div>
            {device && (
              <Card
                variant='outlined'
                style={{ display: 'flex', width: '100%' }}
              >
                <CardContent
                  style={{
                    display: 'flex',
                    width: '100%',
                    position: 'relative',
                  }}
                >
                  {/* LEFT SIDE */}
                  <div style={{ flex: 1, position: 'relative' }}>
                    {/* device info */}
                    {device.unit.status === 'up' ? (
                      <Typography
                        sx={{ mb: 1.5, fontFamily: 'BrightstarSans-Regular' }}
                        color='text.secondary'
                      >
                        {translations.status || 'Status'}:{' '}
                        <span style={{ color: 'green', fontWeight: 'bold' }}>
                          {translations.up || 'UP'}
                        </span>
                      </Typography>
                    ) : (
                      <Typography
                        sx={{ mb: 1.5, fontFamily: 'BrightstarSans-Regular' }}
                        color='text.secondary'
                      >
                        {translations.status || 'Status'}:{' '}
                        <span style={{ color: 'red', fontWeight: 'bold' }}>
                          {translations.down || 'DOWN'}
                        </span>
                      </Typography>
                    )}

                    {cellularCheckPerformed && (
                      <Typography
                        sx={{ mb: 1.5, fontFamily: 'BrightstarSans-Regular' }}
                        color='text.secondary'
                      >
                        {translations.cellularHealth || 'Cellular Health'}:{' '}
                        {cellularCheck < -105 ? (
                          <span
                            style={{
                              color: 'red',
                              fontFamily: 'BrightstarSans-Bold',
                            }}
                          >
                            {translations.bad || 'BAD'}
                          </span>
                        ) : cellularCheck > -105 && cellularCheck < -95 ? (
                          <span
                            style={{
                              color: 'orange',
                              fontFamily: 'BrightstarSans-Bold',
                            }}
                          >
                            {translations.fair || 'FAIR'}
                          </span>
                        ) : (
                          <span
                            style={{
                              color: 'green',
                              fontFamily: 'BrightstarSans-Bold',
                            }}
                          >
                            {translations.good || 'GOOD'}
                          </span>
                        )}
                      </Typography>
                    )}

                    <Typography
                      sx={{ mb: 1.5, fontFamily: 'BrightstarSans-Regular' }}
                      color='text.secondary'
                    >
                      {translations.hostname || 'Hostname'}:{' '}
                      <span
                        style={{
                          color: '#154db4',
                          fontFamily: 'BrightstarSans-Regular',
                        }}
                      >
                        {device.unit.hostname.toUpperCase()}
                      </span>
                    </Typography>
                    <Typography
                      sx={{ mb: 1.5, fontFamily: 'BrightstarSans-Regular' }}
                      color='text.secondary'
                    >
                      {translations.serial}:{' '}
                      <span
                        style={{
                          color: '#154db4',
                          fontFamily: 'BrightstarSans-Regular',
                        }}
                      >
                        {device.unit.serial}
                      </span>
                    </Typography>
                    <Typography
                      sx={{ mb: 1.5, fontFamily: 'BrightstarSans-Regular' }}
                      color='text.secondary'
                    >
                      {translations.model}:{' '}
                      <span
                        style={{
                          color: '#154db4',
                          fontFamily: 'BrightstarSans-Regular',
                        }}
                      >
                        {device.unit.model}
                      </span>
                    </Typography>
                    <Typography
                      sx={{ mb: 1.5, fontFamily: 'BrightstarSans-Regular' }}
                      color='text.secondary'
                    >
                      {translations.deviceType}:{' '}
                      <span
                        style={{
                          color: '#154db4',
                          fontFamily: 'BrightstarSans-Regular',
                        }}
                      >
                        {device.unit.deviceType}
                      </span>
                    </Typography>
                    <Typography
                      sx={{ mb: 1.5, fontFamily: 'BrightstarSans-Regular' }}
                      color='text.secondary'
                    >
                      {translations.softwareVersion}:{' '}
                      <span
                        style={{
                          color: '#154db4',
                          fontFamily: 'BrightstarSans-Regular',
                        }}
                      >
                        {device.unit.kernelVersion}
                      </span>
                    </Typography>
                    <Typography
                      sx={{ mb: 1.5, fontFamily: 'BrightstarSans-Regular' }}
                      color='text.secondary'
                    >
                      {translations.location || 'Location'}:
                    </Typography>
                    <Typography
                      sx={{ mb: 1.5, fontFamily: 'BrightstarSans-Regular' }}
                      color='text.secondary'
                    >
                      <span
                        style={{
                          color: '#154db4',
                          fontFamily: 'BrightstarSans-Regular',
                        }}
                      >
                        {device.siteInfo.sitename}
                      </span>
                    </Typography>
                    <Typography
                      sx={{ mb: 1.5, fontFamily: 'BrightstarSans-Regular' }}
                      color='text.secondary'
                    >
                      <span
                        style={{
                          color: '#154db4',
                          fontFamily: 'BrightstarSans-Regular',
                        }}
                      >
                        {device.siteInfo.street1}
                      </span>
                    </Typography>
                    <Typography
                      sx={{ mb: 1.5, fontFamily: 'BrightstarSans-Regular' }}
                      color='text.secondary'
                    >
                      <span
                        style={{
                          color: '#154db4',
                          fontFamily: 'BrightstarSans-Regular',
                        }}
                      >
                        {device.siteInfo.street2}
                      </span>
                    </Typography>
                    <Typography
                      sx={{ mb: 1.5, fontFamily: 'BrightstarSans-Regular' }}
                      color='text.secondary'
                    >
                      <span
                        style={{
                          color: '#154db4',
                          fontFamily: 'BrightstarSans-Regular',
                        }}
                      >
                        {device.siteInfo.city}, {device.siteInfo.state}{' '}
                        {device.siteInfo.zip}
                      </span>
                    </Typography>
                    <Typography
                      sx={{ mb: 1.5, fontFamily: 'BrightstarSans-Regular' }}
                      color='text.secondary'
                    >
                      {translations.coordinates || 'Coordinates'}:{' '}
                      <span
                        style={{
                          color: '#154db4',
                          fontFamily: 'BrightstarSans-Regular',
                        }}
                      >
                        {device.unit.lng}, {device.unit.lat}
                      </span>
                    </Typography>
                    <Typography
                      sx={{ mb: 1.5, fontFamily: 'BrightstarSans-Regular' }}
                      color='text.secondary'
                    >
                      {translations.lastStatusChange || 'Last Status Change'}:{' '}
                      <span
                        style={{
                          color: '#154db4',
                          fontFamily: 'BrightstarSans-Regular',
                        }}
                      >
                        {moment
                          .unix(device.unit.time.lastStatusChange)
                          .format('dddd, MMMM Do, YYYY h:mm A')}
                      </span>
                    </Typography>
                    <Typography
                      sx={{ mb: 1.5, fontFamily: 'BrightstarSans-Regular' }}
                      color='text.secondary'
                    >
                      {translations.lastPowerStatusUpdate ||
                        'Last Power Status Update'}
                      :{' '}
                      <span
                        style={{
                          color: '#154db4',
                          fontFamily: 'BrightstarSans-Regular',
                        }}
                      >
                        {moment
                          .unix(device.unit.time.last_power_status_update)
                          .format('dddd, MMMM Do, YYYY h:mm A')}
                      </span>
                    </Typography>

                    {/* Spinner over left side */}
                    {rebootLoading && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          zIndex: 10,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <div
                          style={{
                            width: '50px',
                            height: '50px',
                            border: '6px solid #ccc',
                            borderTop: '6px solid #1976d2',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                          }}
                        ></div>
                        <p
                          style={{
                            marginTop: '10px',
                            color: '#333',
                            fontWeight: 'bold',
                            fontFamily: 'BrightstarSans-Regular',
                          }}
                        >
                          Rebooting Device...
                        </p>
                        <style>
                          {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
                        </style>
                      </div>
                    )}
                  </div>

                  {/* RIGHT SIDE: Map */}
                  <div
                    style={{ width: '50%', marginLeft: '20px', flexShrink: 0 }}
                  >
                    {lat !== null && lng !== null ? (
                      <div className='fade-in'>
                        <MapContainer
                          center={[lat, lng]}
                          zoom={17}
                          style={{ height: '50vh', width: '100%' }}
                        >
                          <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">'
                            url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                          />
                          <Marker position={[lat, lng]} icon={customIcon} />
                        </MapContainer>
                      </div>
                    ) : (
                      <div
                        style={{
                          height: '50vh',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#777',
                          fontSize: '1.2rem',
                        }}
                      >
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='48'
                          height='48'
                          fill='currentColor'
                          viewBox='0 0 16 16'
                          style={{ marginBottom: '10px' }}
                        >
                          <path d='M8 0a5.5 5.5 0 0 0-5.5 5.5c0 3.04 4.9 9.18 5.14 9.49a.5.5 0 0 0 .72 0c.24-.31 5.14-6.45 5.14-9.49A5.5 5.5 0 0 0 8 0zm0 8a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z' />
                        </svg>
                        No map location available
                      </div>
                    )}
                  </div>
                </CardContent>

                {/* BUTTONS Area */}
                <div
                  style={{
                    borderLeft: '1px solid #ccc',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <Tooltip title={translations.pingDevice || 'Ping Device'}>
                    <IconButton
                      onClick={() => handlePing()}
                      disabled={rebootLoading}
                    >
                      <SensorsIcon
                        sx={{ color: rebootLoading ? 'grey' : 'green' }}
                      />
                    </IconButton>
                  </Tooltip>

                  <Tooltip
                    title={
                      translations.cellularHealthCheck ||
                      'Cellular Health Check'
                    }
                  >
                    <IconButton
                      onClick={() => handleCellularCheck()}
                      disabled={rebootLoading}
                    >
                      <HealthAndSafetyIcon
                        sx={{ color: rebootLoading ? 'grey' : 'blue' }}
                      />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={translations.rebootDevice || 'Reboot Device'}>
                    <IconButton
                      onClick={() => handleReboot()}
                      sx={{ color: 'red' }}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
      <Snackbar
        open={isSnackbarOpen}
        autoHideDuration={7500}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          variant='filled'
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Dialog
        open={dialogOpen}
        onClose={handleCancelReboot}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle
          id='alert-dialog-title'
          sx={{ fontFamily: 'BrightstarSans-Regular' }}
        >
          <WarningIcon sx={{ color: 'red' }} />
          {' Warning!'}
        </DialogTitle>
        <DialogContent>
          <Typography
            variant='body2'
            sx={{ fontFamily: 'BrightstarSans-Regular' }}
          >
            Rebooting this device will disrupt service for the customer. Are you
            sure you want to do this?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelReboot}
            color='primary'
            sx={{ fontFamily: 'BrightstarSans-Regular' }}
          >
            No
          </Button>
          <Button
            onClick={handleConfirmReboot}
            color='primary'
            sx={{ fontFamily: 'BrightstarSans-Regular' }}
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VentusSearch;
